import {
    Autocomplete,
    AutocompleteInputChangeReason,
    CircularProgress,
    FormControl,
    ListItemIcon,
    TextField
} from "@mui/material";
import React from "react";
import { useController, UseControllerProps } from "react-hook-form";

export interface IDropDownOptionsWithIcon {
    id: string | number;
    value: string;
    description: string;
    group?: string;
    icon?: React.ReactNode;
    showIcon?: boolean;
}

interface IProps extends UseControllerProps {
    label: string;
    items: IDropDownOptionsWithIcon[];
    size?: 'small' | 'medium';
    disabled?: boolean;
    loading?: boolean;
    disabledItems?: (string | number)[];
    onChange?: (value: any) => void;
    onInputFieldChange?: (value: string) => void;
    style?: React.CSSProperties;
    useManualFiltering?: boolean;
    preSelectedItemId?: string | number;
}

const AutocompleteWithIcon = (props: IProps) => {
    const filterOptions = (options: IDropDownOptionsWithIcon[], { inputValue }: { inputValue: string }) => {
        return options.filter((option) => {
            const mainTextMatch = option.value.toLowerCase().includes(inputValue.toLowerCase());
            const descriptionMatch = option.description?.toLowerCase().includes(inputValue.toLowerCase()) ?? false;
            return mainTextMatch || descriptionMatch;
        });
    };
    const [selectedItem, setSelectedItem] = React.useState<IDropDownOptionsWithIcon | null>(null);
    const { fieldState, field } = useController({ ...props, defaultValue: null });

    const handleInputChange = (event: React.ChangeEvent<{}>, value: string, reason: AutocompleteInputChangeReason) => {
        if (reason === 'input' || reason === 'clear') {
            if (props.onInputFieldChange) {
                props.onInputFieldChange(value);
            }
        } else {
            if (props.onInputFieldChange) {
                props.onInputFieldChange('');
            }
        }
    };

    React.useEffect(() => {
        if (props.preSelectedItemId) {
            const selected = props.items.find(item => item.id === props.preSelectedItemId);
            setSelectedItem(selected || null);
        }
    }, [props.preSelectedItemId, props.items]);

    return (
        <FormControl fullWidth error={!!fieldState.error}>
            <Autocomplete
                {...props}
                {...field}
                onChange={(event, value) => {
                    setSelectedItem(value);
                    field.onChange(value);
                    if (props.onChange) {
                        props.onChange(value);
                    }
                }}
                onInputChange={handleInputChange}
                isOptionEqualToValue={(option, value: any) => option.id === value.id}
                options={props.items}
                filterOptions={(options, { inputValue }) => {
                    if (props.useManualFiltering === true) {
                        return options;
                    } else {
                        return filterOptions(options, { inputValue });
                    }
                }}
                groupBy={(option) => option.group || null}
                renderGroup={(params) => (
                    <div key={params.key}>
                        <div style={{ fontWeight: 'bold' }}>{params.group}</div>
                        {params.children}
                    </div>
                )}
                getOptionLabel={(option) => `${option.value} (${option.description})`}
                loading={props.loading}
                renderOption={(props, option) => {
                    return (
                        <li {...props} key={`${option.id}-${option.description}`}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ minWidth: '24px', marginRight: '5px' }}>
                                    {option.showIcon && option.icon ? option.icon : <span style={{ width: 24 }} />}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div>
                                        {option.value}
                                    </div>
                                    <div>
                                        <span style={{ fontStyle: 'italic', color: 'grey' }}>{option.description}</span>
                                    </div>
                                </div>
                            </div>
                        </li>
                    );
                }}

                renderInput={(params) => (
                    <TextField
                        {...params}
                        helperText={fieldState.error?.message}
                        label={props.label}
                        variant="outlined"
                        error={!!fieldState.error}
                        size={props.size || 'medium'}
                        fullWidth
                        value={selectedItem ? `${selectedItem.value} (${selectedItem.description})` : ''}
                        InputProps={{
                            ...params.InputProps,
                            style: {
                                paddingTop: '10px',
                                paddingBottom: '10px',
                            },
                            startAdornment: selectedItem && selectedItem.icon && (
                                <ListItemIcon style={{ minWidth: '24px', marginRight: '3px', marginLeft: '3px' }}>
                                    {selectedItem.icon}
                                </ListItemIcon>
                            ),
                            endAdornment: (
                                <React.Fragment>
                                    {props.loading ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </React.Fragment>
                            ),
                        }}
                    />
                )}

            />
        </FormControl>
    );
};

export default AutocompleteWithIcon;
